import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import PlusIcon from "../../../../../images/common/add-inside-circle-blue.svg";
import {
  ContactReferenceModel,
  MerchantAccount,
  PaymentMethodModel,
  UpsertPaymentModel,
} from "../models";
import {
  AuthService,
  ContactService,
  MerchantAccountService,
  PaymentMethodService,
  PaymentService,
} from "../services";
import { ApplicationStatus, BillingMethodType } from "../enums";
import { DescriptionCell, InvoicePopupWrapper } from "../styles";
import {
  CustomLabel,
  EditableField,
  FlexContainer,
  IAPIResponse,
  Input,
  Loader,
  ModalComponent,
  SapphireBlueButton,
  TextInputWithDropdown,
  TimeAMPM,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { IAPICreateEditPaymentResponseModel } from "../interfaces";
import RenderContactInfo from "./RenderContactInfo";
import ContactDropDown from "./ContactDropDown";
import MerchantAccountsDropDown from "./MerchantAccountsDropDown";
import BillingMethods from "./BillingMethods";
import PaymentMethodDropdown from "./PaymentMethodDropdown";
import { AccountSettingsStore } from "../store";

type IProps = {
  contactId?: string;
  navigate: NavigateFunction;
  onClose: (refresh: boolean, contactId?: string) => void;
  paymentData: UpsertPaymentModel;
};

const UpsertPaymentPopUp: React.FC<IProps> = ({
  onClose,
  paymentData,
  contactId,
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodModel[]>(
    []
  );
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>(
    []
  );
  const [data, setPaymentData] = React.useState<UpsertPaymentModel>(
    new UpsertPaymentModel({
      ...paymentData,
      isContactRequired:
        !contactId && AuthService.isMindMeContactRoute ? false : true,
    })
  );
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [showDescription, setShowDescription] = React.useState(false);
  const [showLoader, setShowLoader] = React.useState(true);
  const [contacts, setContactsData] = React.useState<ContactReferenceModel[]>(
    []
  );
  const [statementDescription, setStatementDescriptor] = useState({
    isEdit: false,
    value: "",
  });
  const [hasMoreContacts, setHasMoreContacts] = React.useState(false);
  function isUpdate() {
    return data.id !== "";
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        setShowLoader(true);

        await Promise.all([
          fetchPaymentMethods(),
          loadMerchantAccounts(),
          loadContacts(data.contact?.id, data.contact?.mindmeContactId),
        ]);
      } catch (error) {
        console.error("Error in API calls:", error);
      } finally {
        setShowLoader(false);
      }
    };

    loadData();
  }, []);

  async function fetchPaymentMethods() {
    try {
      const response =
        await PaymentMethodService.getPaymentMethods().toPromise();

      if (response?.result) {
        const paymentMethods = response.result.map((paymentMethod) =>
          PaymentMethodModel.deserializeAPIModel(paymentMethod)
        );

        const selectedPaymentMethod =
          PaymentMethodModel.getDefaultSelecedPaymentMethod(
            paymentMethods,
            data.contact?.id || ""
          );

        if (selectedPaymentMethod && !data.paymentMethod) {
          if (data) {
            setTimeout(() => {
              setPaymentData(
                (prev) =>
                  new UpsertPaymentModel({
                    ...prev,
                    paymentMethod: !prev.paymentMethod
                      ? selectedPaymentMethod
                      : prev.paymentMethod,
                  })
              );
            }, 500);
          }
        }
        setPaymentMethods(paymentMethods);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      throw error;
    }
  }

  async function loadContacts(
    paymentContactId?: string,
    mindmeContactId?: string,
    searchValue?: string,
    pageSize: number = 50
  ) {
    try {
      const response = await ContactService.getMindMeContacts(
        AuthService.authData.accountId,
        paymentContactId,
        mindmeContactId,
        searchValue,
        pageSize
      ).toPromise();

      const contacts =
        response?.result?.data?.map((contact) => {
          return ContactReferenceModel.deserializeFromMindMeContacts(contact);
        }) || [];
      setContactsData(contacts);
      setHasMoreContacts(response?.result?.hasMoreData || false);
      const selectedContact = contacts.find(
        (c) =>
          (paymentContactId && c.id === paymentContactId) ||
          (mindmeContactId && c.mindmeContactId === mindmeContactId)
      );
      if (selectedContact) {
        setPaymentData(
          (prevData) =>
            new UpsertPaymentModel({
              ...prevData,
              contact: selectedContact,
            })
        );
      }
    } catch (error) {
      console.error("Error loading contacts:", error);
      throw error;
    }
  }

  async function loadMerchantAccounts() {
    try {
      const response = await MerchantAccountService.getMerchantAccounts(
        AuthService.authData.accountId,
        1,
        10000,
        ""
      ).toPromise();

      if (response?.result) {
        const activeMerchantAccounts = (
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          []
        ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

        const defaultMerchantAccount =
          activeMerchantAccounts?.length > 0
            ? activeMerchantAccounts[0]
            : undefined;

        setTimeout(() => {
          setPaymentData(
            (prevData) =>
              new UpsertPaymentModel({
                ...prevData,
                merchantAccount: !prevData.merchantAccount
                  ? defaultMerchantAccount
                  : prevData.merchantAccount,
              })
          );
        }, 500);

        setMerchantAccounts(activeMerchantAccounts);
      }
    } catch (error) {
      console.error("Error loading merchant accounts:", error);
      throw error;
    }
  }

  const formatAmount = (value: string): string => {
    if (parseFloat(value) < 0 || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(value.toString().replace(/,/g, "") || "0.00"),
      true
    );
  };

  function row(label: string, rightSide: React.ReactNode, className?: string) {
    return (
      <FlexContainer spacing={30} margin="15px" className={className}>
        <CustomLabel>{label}</CustomLabel>
        <FlexContainer alignItems="center" spacing={20}>
          {rightSide}
        </FlexContainer>
      </FlexContainer>
    );
  }

  function updateState(
    key: keyof UpsertPaymentModel,
    value:
      | string
      | boolean
      | number
      | Date
      | MerchantAccount
      | PaymentMethodModel,
    isBlur?: boolean
  ) {
    const clonedData = new UpsertPaymentModel(data);
    switch (key) {
      case "description":
        clonedData.description = value as string;
        break;
      case "amount":
        if (isBlur) {
          value = formatAmount(value as string);
        }
        clonedData.amount = value as string;
        break;
      case "billingMethod":
        clonedData.billingMethod = value as keyof typeof BillingMethodType;
        break;
      case "dueDate":
        clonedData.dueDate = value as string;
        break;
      case "paymentMethod":
        clonedData.paymentMethod = value as PaymentMethodModel;
        break;
      case "merchantAccount":
        clonedData.merchantAccount = value as MerchantAccount;
        break;
      case "newMethodIsPrimary":
        clonedData.newMethodIsPrimary = value as boolean;
        break;
      case "statementDescriptor":
        clonedData.statementDescriptor = value as string;
        setStatementDescriptor({ isEdit: false, value: value as string });
        break;
    }
    clonedData.validate(true);
    setPaymentData(clonedData);
  }

  function textInput(
    type: "text" | "textarea" | "number" | "email" | "date",
    key: keyof UpsertPaymentModel,
    value: string | Date,
    placeHolder: string,
    className: string
  ) {
    return (
      <Input
        type={type}
        value={value}
        error={data.hasError(key)}
        className={className}
        onChange={(e, isBlur) => updateState(key, e.target.value, isBlur)}
        placeholder={placeHolder}
        onDateChange={(date) => updateState(key, date)}
        minDate={new Date()}
      />
    );
  }

  function onSubmit() {
    const success = data.validate(false);
    if (!success) {
      setPaymentData(new UpsertPaymentModel(data));
      return;
    }
    setAPIRequestPending(true);
    const errorMessage = data.id ? "Error updating payment" : "Payment Failed";
    const successResposne = (
      response: IAPIResponse<IAPICreateEditPaymentResponseModel>
    ) => {
      setAPIRequestPending(false);
      if (response.result.success) {
        const successMessage = data.id
          ? "Payment updated Successfully"
          : "Payment Successful";
        if (response.result.isPaymentFailed) {
          ToastService.showError("Payment Failed");
        } else if (response.result.isPaid) {
          ToastService.showSuccess("Payment Successful");
        } else {
          ToastService.showSuccess(successMessage);
        }
        onClose(true, response.result.contactId);
      } else {
        switch (response.result.failureReason) {
          case "AlreadyPaid":
            ToastService.showError("Payment already paid");
            break;
          case "AboutToProcess":
            ToastService.showError("Payment is about to process");
            break;
          case "MerchantAccountNotFound":
            ToastService.showError("Merchant account not found");
            break;
        }
        setAPIRequestPending(false);
        ToastService.showError(errorMessage);
      }
    };
    const errorResponse = (error: any) => {
      console.error(error);
      setAPIRequestPending(false);
      ToastService.showError(errorMessage);
    };
    const mindmeContactId = AuthService.isMindMeContactRoute
      ? AuthService.getMindMeContactId
      : data.contact?.mindmeContactId || "";
    if (data.id) {
      const updateRequest = data.createPaymentAPIModel(
        data.contact?.id || "",
        mindmeContactId
      );
      PaymentService.updatePayment(updateRequest).subscribe({
        next: successResposne,
        error: errorResponse,
      });
    } else {
      const request = data.createPaymentAPIModel(
        data.contact?.id || "",
        mindmeContactId
      );
      PaymentService.savePayment(request).subscribe({
        next: successResposne,
        error: errorResponse,
      });
    }
  }

  function amountRow() {
    return (
      <>
        {row(
          "Payment Amount",
          <CustomLabel>
            {textInput("text", "amount", data.amount, "Enter amount", "")}
          </CustomLabel>
        )}
      </>
    );
  }

  function updateTime(amPm: keyof typeof TimeAMPM, time: string) {
    const clonedData = new UpsertPaymentModel(data);
    clonedData.time = time;
    clonedData.amPm = amPm;
    clonedData.validate(true);
    setPaymentData(clonedData);
  }

  function onContactChange(selectedContact: ContactReferenceModel) {
    const defaultPaymentMethod =
      PaymentMethodModel.getDefaultSelecedPaymentMethod(
        paymentMethods,
        selectedContact.id
      );
    setPaymentData(
      new UpsertPaymentModel({
        ...data,
        contact: selectedContact,
        paymentMethod: defaultPaymentMethod,
        newMethodIsPrimary: !defaultPaymentMethod ? true : false,
      })
    );
  }

  const onRefreshContacts = (
    contactId: string,
    mindmeContactId: string,
    searchValue: string,
    pageSize: number
  ) => {
    loadContacts(contactId, mindmeContactId, searchValue, pageSize);
  };

  function body() {
    if (showLoader) return new Loader(true).loaderComponent;

    const isShowDescription =
      showDescription ||
      data.hasError("description") ||
      data.description.length > 0;

    return (
      <InvoicePopupWrapper className="subscription-popup">
        {!AuthService.isMindMeContactRoute && (
          <>
            {isUpdate() &&
              data.contact &&
              row("Customer", <RenderContactInfo contact={data.contact} />)}
            {(!data.contact || !isUpdate()) &&
              row(
                "Customer",
                <ContactDropDown
                  contacts={contacts}
                  selectedContact={data.contact}
                  hasError={data.hasError("contact")}
                  onSelect={(selectedContact) => {
                    onContactChange(selectedContact);
                  }}
                  hasMoreContacts={hasMoreContacts}
                  onRefresh={(
                    contactId,
                    mindmeContactId,
                    searchValue,
                    pageSize
                  ) => {
                    onRefreshContacts(
                      contactId,
                      mindmeContactId,
                      searchValue,
                      pageSize
                    );
                  }}
                  className="white-bg"
                />
              )}
          </>
        )}
        {row(
          "Billing Method",
          <BillingMethods
            selectedMethod={data.billingMethod}
            onChange={(value) => updateState("billingMethod", value)}
          />
        )}
        {merchantAccounts.length > 1 &&
          row(
            "Merchant Account",
            <MerchantAccountsDropDown
              selectedMerchantAccount={data.merchantAccount}
              merchantAccounts={merchantAccounts}
              showError={data.hasError("merchantAccount")}
              onChange={(option) => updateState("merchantAccount", option)}
              className="white-bg "
            />
          )}
        {row(
          "Payment Method",
          <PaymentMethodDropdown
            merchantAccount={
              data.merchantAccount?.merchantAccountId
                ? data.merchantAccount
                : merchantAccounts[0]
            }
            selectedPaymentMethod={data.paymentMethod}
            paymentMethods={paymentMethods}
            contactId={data.contact?.id || ""}
            isRenderWithoutContact={
              (!contactId && AuthService.isMindMeContactRoute) ||
              data.contact?.mindmeContactId
                ? true
                : false
            }
            onChange={(option) => updateState("paymentMethod", option)}
            hasError={data.hasError("paymentMethod")}
            className="invoice-payment-method"
            hasErrorInNewPaymentMethod={data.hasError(
              "newPaymentGatewayMethodId"
            )}
            onNewPaymentMethod={(
              isNewPaymentMethod,
              newPaymentGatewayMethodId
            ) => {
              onNewPaymentMethod(isNewPaymentMethod, newPaymentGatewayMethodId);
            }}
            isNewPaymentMethodPrimary={data.newMethodIsPrimary}
            updateNewMethodAsPrimary={(value: boolean) =>
              updateState("newMethodIsPrimary", value)
            }
            isLodingInProcess={showLoader}
            headerClass="subscription-payment-method"
          />
        )}
        {amountRow()}
        {row(
          "Payment Date",
          <>
            {textInput(
              "date",
              "dueDate",
              UtilityMethods.toLocalISOString(new Date(data.dueDate)),
              "Enter due date",
              "invoice-payment-datepicker"
            )}
            {data.dueDate &&
              new Date().toDateString() !==
                new Date(data.dueDate).toDateString() && (
                <TextInputWithDropdown
                  textInputValue={data.time}
                  dropdownValue={data.amPm}
                  onTextChange={(value) => updateTime(data.amPm, value)}
                  onDropdownChange={(option) =>
                    updateTime(option.value, data.time)
                  }
                  className="invoice-timezone subscription-timezone"
                  dropdownOptions={[
                    Object.keys(TimeAMPM).map((key) => ({
                      value: key,
                      label: TimeAMPM[key as keyof typeof TimeAMPM],
                    })),
                  ]}
                  error={data.hasError("time")}
                  placeholderForTextInput="hh:mm"
                  isTime={true}
                />
              )}
            <FlexContainer alignItems="center">
              <CustomLabel>
                {AccountSettingsStore.generalSettings.timeZone.abbreviation ||
                  ""}
              </CustomLabel>
            </FlexContainer>
          </>
        )}
        {row(
          "Description",
          isShowDescription ? (
            <CustomLabel>
              {textInput(
                "textarea",
                "description",
                data.description,
                "Enter Description",
                ""
              )}
            </CustomLabel>
          ) : (
            <FlexContainer
              alignItems="center"
              spacing={10}
              className="clickable"
            >
              <img
                src={PlusIcon}
                alt="description"
                className="desc-plus-icon"
                onClick={() => setShowDescription(true)}
              />
              <CustomLabel
                color="#556BB8"
                className="clickable"
                lineHeight="40px"
                onClick={() => setShowDescription(true)}
              >
                Add
              </CustomLabel>
            </FlexContainer>
          ),
          isShowDescription ? "desc-row-margin" : "desc-row-margin align-center"
        )}
        {/* {row(
          "Statement Descriptor",
          <DescriptionCell>
            <EditableField
              value={statementDescription.value || data.statementDescriptor}
              isEdit={statementDescription.isEdit}
              onEditClick={() =>
                setStatementDescriptor({
                  isEdit: true,
                  value: data.statementDescriptor,
                })
              }
              onSaveClick={() =>
                updateState("statementDescriptor", statementDescription.value)
              }
              onCancelClick={() =>
                setStatementDescriptor({ isEdit: false, value: "" })
              }
              onChange={(e) =>
                setStatementDescriptor({ isEdit: true, value: e.target.value })
              }
              placeholder="Enter Name"
            />
          </DescriptionCell>,
          statementDescription.isEdit ? "" : "align-center"
        )} */}
      </InvoicePopupWrapper>
    );
  }

  function onNewPaymentMethod(isNewPaymentMethod: boolean, token: string) {
    setPaymentData(
      (prevData) =>
        new UpsertPaymentModel({
          ...prevData,
          isNewPaymentMethod: isNewPaymentMethod,
          newPaymentGatewayMethodId: token,
        })
    );
  }
  return (
    <ModalComponent
      title={isUpdate() ? "Modify Scheduled Payment" : "New Payment"}
      titleFontSize="22px"
      bodyPadding="40px 50px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="900px"
      bodyBackgroundColor="#fff"
      footerBackground="#F4F7FB"
      footerHeight="auto"
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <SapphireBlueButton
            minWidth="120px"
            height="30px"
            onClick={() => onClose(false)}
          >
            Cancel
          </SapphireBlueButton>
          <SapphireBlueButton
            disabled={isAPIRequestPending}
            primary
            minWidth="120px"
            height="30px"
            onClick={() => onSubmit()}
          >
            {data.isShowPayButton()
              ? "Pay Now"
              : isUpdate()
                ? "Update"
                : "Schedule"}
          </SapphireBlueButton>
        </FlexContainer>
      }
    >
      {body()}
    </ModalComponent>
  );
};

export default withRouter(observer(UpsertPaymentPopUp));
